import {
    DocumentType,
    index,
    modelOptions,
    mongoose,
    PropType,
    post,
    pre,
    prop,
    Ref,
    Severity,
} from "@typegoose/typegoose";
import { z } from "zod";
import { sensorClusterConfigs } from "../../Configurations/SensorConfigurations/sensorClusters/sensorClusterConfigs.js";
import { minuteMs } from "../../timeConstants.js";
import {
    ENTITY_NAME_MAX_LENGTH,
    emptyableEntityNameZodValidator,
    milliseconds,
    ObjectIdString,
} from "../../validators/index.js";
import { getZodSchemaFromClass, SensorClusterConfigKeyString, validate } from "../../zod-decorator/index.js";
import { RecentSensorDataPoint } from "../commonSubSchemas/recentSensorDataPoint.js";
import { SensorClusterAddress } from "../commonSubSchemas/sensorClusterAddress.js";
import { SensorElementAddress } from "../commonSubSchemas/sensorElementAddress.js";
import { VertexInfo } from "../commonSubSchemas/vertexInfo.js";
import { SensorSpecifier } from "../device/subSchemas/sensorSpecifier.js";
import type { Organization } from "../organization/organization.js";
import { ScdDocument } from "../scdDocument/scdDocument.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import { SensorElement } from "../sensorElement/sensorElement.js";
import type { SensorClusterFieldsWithDefaults, SensorClusterSchemaTypes } from "../types/sensorCluster.d.js";
import { trackMixed } from "../utils/mixedSchemaSavingUtils.js";
import { type DtoValidatorTypes } from "../validatableClass.js";
import { Zone } from "../zone/zone.js";

export type SensorClusterConfigKey = keyof typeof sensorClusterConfigs;

/**
 * How a cluster came to exist:
 * - `fixedDevicePort` — synthesized from a fixed port in the device type's `sensorPortConfiguration`.
 * - `userConfigured` — mirrors what a user wired to an open port (today via `device.connectedSensors`).
 */
export const SENSOR_CLUSTER_ORIGINS = ["fixedDevicePort", "userConfigured"] as const;
export type SensorClusterOrigin = (typeof SENSOR_CLUSTER_ORIGINS)[number];

const SCHEMA_KEY = "SensorCluster" as const;

/**
 * Represents a physical sensor product attached to a specific port + channel on a device.
 *
 * A SensorCluster is the top-level SCD Type 2 record for a connected sensor. It stores:
 * - where it lives on its host (`address`: host ref, port, channel, communication mode)
 * - which cluster configuration it instantiates (`sensorClusterConfigKey`)
 * - the user-set parameters for that configuration (`parameters`)
 *
 * ## Sensor elements
 *
 * The set of sensor elements (measurement channels) this cluster produces is derived
 * on demand via `SensorCluster.getters.sensorElements(cluster)`. Elements are not
 * persisted — they are computed deterministically from `sensorClusterConfigKey` and
 * `parameters` by the cluster configuration's `generateSensorElements` function.
 *
 * ## Partial unique index
 *
 * At most one active non-deleted cluster may occupy a given (device, port, channel) slot.
 */
@index(
    {
        "address.hostReference.targetId": 1,
        "address.hostReference.targetCollection": 1,
        "address.portId": 1,
        "address.channel": 1,
    },
    { unique: true, partialFilterExpression: { versionIsCurrent: true, deleted: false } },
)
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "sensorClusters",
        _id: true,
    },
})
/**
 * Derive `address.communicationMode` from the cluster's configuration before each save,
 * so callers never have to supply it and it stays in sync with `sensorClusterConfigKey`.
 */
@pre<SensorCluster>("save", function (this: DocumentType<SensorCluster>) {
    const configuredCommunicationMode = sensorClusterConfigs[this.sensorClusterConfigKey]?.communicationMode;
    if (configuredCommunicationMode == null) return;
    this.address.communicationMode = configuredCommunicationMode;
})
@post<SchemaBase<SensorCluster>>(...addSchemaKeyHook(SCHEMA_KEY))
export class SensorCluster extends ScdDocument<SensorCluster, SensorClusterFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    /**
     * Inherited from original sensor specifier. We want to get rid of "default" as a valid
     * option and ensure zones are set explicitly as soon as possible.
     */
    @validate({ zodValidator: z.array(z.union([ObjectIdString, z.literal("default")])).optional() })
    @prop({ type: () => String, required: true, default: ["default"] }, PropType.ARRAY)
    public connectedZones: (Ref<Zone> | "default")[] = ["default"];

    /** Inherited from original sensor specifier. Acceptable sensor reading range for SMS alerts. */
    @validate({ zodValidator: z.array(z.number()).optional() })
    @prop({ type: () => Number, required: false }, PropType.ARRAY)
    public acceptableValueRange?: number[];

    /**
     * Inherited from original sensor specifier. Per-valve-state acceptable reading ranges.
     *
     * acceptableRangeByValveState[0b00] — both valves closed
     * acceptableRangeByValveState[0b11] — both valves open
     * acceptableRangeByValveState[0b10] — V2 open, V1 closed
     */
    // sparse by design: indexed by valve-state bitmask, so states with no configured range are
    // stored as null. Verification reads `?.[state] || undefined` and skips the sensor when absent.
    @validate({ zodValidator: z.array(z.array(z.number()).nullable()).optional() })
    @prop({ type: () => Number, dim: 2, required: false }, PropType.ARRAY)
    public acceptableRangeByValveState?: (number[] | null)[];

    /**
     * Inherited from original sensor specifier. Maximum ramp-up time after a valve transition
     * before verification logic considers the sensor settled.
     */
    @validate({ zodValidator: milliseconds.optional() })
    @prop({ type: () => Number, required: true, default: minuteMs * 3 })
    public acceptableRampUpTimeMs: number = minuteMs * 3;

    /**
     * Inherited from original sensor specifier. Opt-in trailing-edge ramp-down grace (0 = disabled).
     * Within this window before the open state's scheduled close, verification is held rather than failed.
     */
    @validate({ zodValidator: milliseconds.optional() })
    @prop({ type: () => Number, required: false, default: 0 })
    public acceptableRampDownTimeMs: number = 0;
    /** Inherited from original sensor specifier. Enables/disables threshold-based verification. */
    @validate({ zodValidator: z.boolean().optional() })
    @prop({ type: () => Boolean, required: false })
    public useForVerification?: boolean;

    /** Inherited from original sensor specifier. Most recently cached sensor reading. */
    @validate({
        zodValidator: z.object({ date: z.coerce.date(), dataType: z.string(), value: z.number() }).optional(),
        responseOnly: true,
    })
    @prop({ type: () => RecentSensorDataPoint, required: false })
    lastReading?: RecentSensorDataPoint;

    /** Inherited from original sensor specifier. Calculated by RefreshSensingPeriod subroutine. */
    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: false })
    public uplinkPeriod?: number;

    /** Inherited from original sensor specifier. Calculated by RefreshSensingPeriod subroutine. */
    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: false })
    public readingPeriod?: number;

    /**
     * Where this cluster lives on its host: host reference, port, channel, and communication
     * mode. `communicationMode` is derived from `sensorClusterType` in a pre-save hook —
     * callers do not set it.
     */
    @validate({ childClass: SensorClusterAddress })
    @prop({ type: () => SensorClusterAddress, required: true, _id: false })
    public address!: SensorClusterAddress;

    /**
     * Key into `sensorClusterConfigs` identifying which sensor product this cluster
     * represents. Determines the shape of `parameters` and the elements generated.
     */
    @validate({ zodValidator: SensorClusterConfigKeyString })
    @prop({
        type: () => String,
        required: true,
        validate: {
            validator: (value: string): boolean => value in sensorClusterConfigs,
            message: (props: { value: string }) =>
                `Unknown sensorClusterConfigKey "${props.value}": no entry in sensorClusterConfigs.`,
        },
    })
    public sensorClusterConfigKey!: SensorClusterConfigKey;

    /**
     * Whether this cluster was generated from the device type's fixed port configuration or
     * mirrors a user-configured open port. Server-derived on every generated write; absent only
     * on documents written before this field existed, which the reconciliation sweep repairs.
     */
    @validate({ zodValidator: z.enum(SENSOR_CLUSTER_ORIGINS), responseOnly: true })
    @prop({ type: () => String, required: true, enum: SENSOR_CLUSTER_ORIGINS })
    public origin!: SensorClusterOrigin;

    /**
     * User-set configurable parameters for this cluster. Defaults to `{}` for clusters
     * with no configurable parameters (fixed calibration).
     */
    @validate({ zodValidator: z.record(z.string(), z.union([z.number(), z.string()])).optional() })
    @trackMixed
    @prop({ type: () => Object, allowMixed: Severity.ALLOW, required: true, default: {} })
    public parameters: Record<string, number | string> = {};

    /** Optional user-facing label for this cluster instance (e.g. "Inlet flowmeter"). */
    @validate({ zodValidator: emptyableEntityNameZodValidator })
    @prop({ type: () => String, required: false, maxlength: ENTITY_NAME_MAX_LENGTH })
    public name?: string;

    /**
     * Cached copy of the host asset's position (e.g. the device's lat/lng). Updated
     * by the service layer when the host asset's position changes. Used as a fallback
     * when `position` is not explicitly set on this cluster.
     */
    @validate({ childClass: VertexInfo, childClassTransform: (object) => object.optional() })
    @prop({ type: () => VertexInfo, required: false, _id: false })
    public hostPosition?: VertexInfo;

    /**
     * Physical installation position of this cluster. `depth` is below ground in
     * meters (positive = below surface; 0 = ground level, which is the default for
     * a cluster installed at the surface). When absent, `hostPosition` is used as
     * the base for deriving element positions.
     */
    @validate({ childClass: VertexInfo, childClassTransform: (object) => object.optional() })
    @prop({ type: () => VertexInfo, required: false, _id: false })
    public position?: VertexInfo;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, required: true, ref: "Organization" })
    public owner!: Ref<Organization>;

    static getters = {
        sensorElements: (self: SensorCluster): SensorElement[] => {
            const config = sensorClusterConfigs[self.sensorClusterConfigKey];
            const outputs = config.generateSensorElements(self.parameters as any);
            const basePosition = self.position ?? self.hostPosition;
            return outputs.map(
                (output) =>
                    new SensorElement({
                        address: new SensorElementAddress({
                            hostReference: self.address.hostReference,
                            portId: self.address.portId,
                            communicationMode: self.address.communicationMode,
                            channel: self.address.channel,
                            subChannel: output.subChannel,
                        }),
                        sensorElementConfigKey: output.sensorElementConfigKey,
                        parameters: output.parameters as Record<string, number | string>,
                        position: new VertexInfo({
                            lat: output.position?.lat ?? basePosition?.lat,
                            lng: output.position?.lng ?? basePosition?.lng,
                            // 0 is intentional: elements default to surface level unless the
                            // element config explicitly sets a depth.
                            depth: output.position?.depth ?? 0,
                        }),
                    }),
            );
        },

        legacyConnectedSensor: (self: SensorCluster): SensorSpecifier | null => {
            const config = sensorClusterConfigs[self.sensorClusterConfigKey];
            if (!config.migrationProperties) return null;

            const { legacySensorType, clusterParametersToLegacyParameters } = config.migrationProperties;
            const sensorParameters = clusterParametersToLegacyParameters(self.parameters as any);

            return new SensorSpecifier({
                sensorType: legacySensorType,
                sensorParameters,
                connectedZones: self.connectedZones,
                acceptableValueRange: self.acceptableValueRange,
                acceptableRangeByValveState: self.acceptableRangeByValveState,
                acceptableRampUpTimeMs: self.acceptableRampUpTimeMs,
                acceptableRampDownTimeMs: self.acceptableRampDownTimeMs,
                useForVerification: self.useForVerification,
                lastReading: self.lastReading,
                uplinkPeriod: self.uplinkPeriod,
                readingPeriod: self.readingPeriod,
            });
        },
    } as const satisfies Record<string, (self: SensorCluster) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<SensorClusterSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<SensorCluster, SensorClusterFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

SensorCluster.dtoValidators = getZodSchemaFromClass(
    SensorCluster,
) as unknown as DtoValidatorTypes<SensorClusterSchemaTypes>;
